import { Key, RefObject, useCallback } from 'react';
import { useOverflowObserver } from './useOverflowObserver';
import { TabElementType } from './UnderlinedTabsList';
import { scrollIntoViewHorizontal } from './scrollIntoViewHorizontal';

export type ScrollPosition = 'start' | 'end' | 'nearest';

type Direction = 'left' | 'right';

export interface TabRefsMap {
  [key: Key]: React.RefObject<TabElementType>;
}

export const useScrollableTabs = ({
  tabRefs,
  scrollContainerRef,
}: {
  tabRefs: RefObject<TabElementType>[];
  scrollContainerRef: RefObject<HTMLDivElement>;
}): {
  scrollToDirection: (direction: Direction) => void;
  intersectionObserver: IntersectionObserver | null;
  scrollTabIntoView: (tabElement: TabElementType, isSmooth?: boolean) => void;
} => {
  const horizontalOffset =
    (scrollContainerRef.current &&
      parseInt(
        window
          .getComputedStyle(scrollContainerRef.current)
          .getPropertyValue('scroll-padding-left')
      )) ||
    0;

  const { overflowedElements, intersectionObserver } = useOverflowObserver(
    scrollContainerRef,
    { horizontalOffset }
  );

  const scrollTabIntoView = (tab: TabElementType, isSmooth?: boolean) => {
    if (scrollContainerRef.current) {
      scrollIntoViewHorizontal(
        scrollContainerRef.current,
        tab,
        horizontalOffset,
        isSmooth
      );
    }
  };

  const scrollTabIntoViewPosition = (
    tab: TabElementType,
    position: ScrollPosition = 'nearest'
  ) => {
    tab?.scrollIntoView({
      behavior: 'smooth',
      inline: position,
      block: 'nearest',
    });
  };

  const getLongestAndShortestTab = useCallback(() => {
    if (!tabRefs || tabRefs.length === 0)
      return { longest: null, shortest: null };

    let longestTabRef = tabRefs[0];
    let shortestTabRef = tabRefs[0];

    tabRefs.forEach((ref) => {
      if (ref?.current) {
        if (
          ref.current.offsetWidth > (longestTabRef?.current?.offsetWidth || 0)
        ) {
          longestTabRef = ref;
        }
        if (
          ref.current.offsetWidth <
          (shortestTabRef?.current?.offsetWidth || Infinity)
        ) {
          shortestTabRef = ref;
        }
      }
    });

    return {
      longestTab: longestTabRef?.current,
      shortestTab: shortestTabRef?.current,
    };
  }, [tabRefs]);

  const scrollToDirection = useCallback(
    (direction: Direction) => {
      const scrollContainer = scrollContainerRef.current;

      const { longestTab, shortestTab } = getLongestAndShortestTab();

      const visibleElements = tabRefs.filter(
        (ref) => ref.current && !overflowedElements.has(ref.current)
      );

      if (longestTab && scrollContainer && visibleElements.length > 1) {
        const targetElementIndex =
          direction === 'left'
            ? tabRefs.indexOf(visibleElements[0])
            : tabRefs.indexOf(visibleElements[visibleElements.length - 1]);

        const targetElement = tabRefs[targetElementIndex]?.current;

        if (targetElement) {
          scrollTabIntoViewPosition(
            targetElement,
            direction === 'left' ? 'end' : 'start'
          );
        }
      } else {
        if (visibleElements.length > 0) {
          const lastVisibleTabIndex =
            direction === 'left'
              ? tabRefs.indexOf(visibleElements[0])
              : tabRefs.indexOf(visibleElements[visibleElements.length - 1]);

          const targetElement =
            direction === 'left'
              ? tabRefs[lastVisibleTabIndex - 1]?.current
              : tabRefs[lastVisibleTabIndex + 1]?.current;

          if (targetElement) {
            scrollTabIntoViewPosition(
              targetElement,
              direction === 'left' ? 'end' : 'start'
            );
          }
        } else {
          if (scrollContainerRef.current && shortestTab) {
            scrollContainerRef.current.scrollBy({
              left:
                direction === 'left'
                  ? -shortestTab?.offsetWidth
                  : shortestTab?.offsetWidth,
              behavior: 'smooth',
            });
          }
        }
      }
    },
    [tabRefs, overflowedElements]
  );
  return {
    scrollToDirection,
    intersectionObserver,
    scrollTabIntoView,
  };
};
